import { useState } from 'react';
import { Card, Button, Input, Textarea, Label } from './../ui';
import { Plus, Pencil, Trash2, Check, X } from 'lucide-react';
import type { CommunityServiceEntry } from '@scholarship-app/types';

interface Props {
  entries: CommunityServiceEntry[];
  editing: boolean;
  onChange: (next: CommunityServiceEntry[]) => void;
}

const empty = (): CommunityServiceEntry => ({
  organization: '',
  role: '',
  start_date: '',
  end_date: null,
  hours: 0,
  description: '',
});

export function ProfileCommunityServiceEditor({ entries, editing, onChange }: Props) {
  const [editIndex, setEditIndex] = useState<number | null>(null);
  const [adding, setAdding] = useState(false);
  const [draft, setDraft] = useState<CommunityServiceEntry | null>(null);

  function startAdd() {
    setAdding(true);
    setEditIndex(null);
    setDraft(empty());
  }
  function startEdit(i: number) {
    setAdding(false);
    setEditIndex(i);
    setDraft({ ...entries[i] });
  }
  function cancel() {
    setAdding(false);
    setEditIndex(null);
    setDraft(null);
  }
  function save() {
    if (!draft) return;
    if (adding) {
      onChange([...entries, draft]);
    } else if (editIndex !== null) {
      const next = [...entries];
      next[editIndex] = draft;
      onChange(next);
    }
    cancel();
  }
  function remove(i: number) {
    if (!confirm(`Delete community service entry: "${entries[i].organization}"?`)) return;
    onChange(entries.filter((_, idx) => idx !== i));
  }

  return (
    <div className="space-y-2">
      {entries.length === 0 && !adding && (
        <p className="text-sm text-soft">No community service entries yet.</p>
      )}
      {entries.map((cs, i) => (
        <div key={i}>
          {editIndex === i && draft ? (
            <CommunityServiceForm draft={draft} setDraft={setDraft} onSave={save} onCancel={cancel} />
          ) : (
            <div className="flex items-start justify-between gap-2 bg-cream rounded-md px-3 py-2">
              <div className="text-sm flex-1 min-w-0">
                <div className="font-medium">{cs.organization || '(no organization)'}</div>
                <div className="text-xs text-soft">
                  {cs.role}{cs.role ? ' · ' : ''}{cs.hours ?? 0} hours
                  {cs.start_date ? ` · started ${cs.start_date}` : ''}
                  {cs.end_date ? ` · ended ${cs.end_date}` : (cs.start_date ? ' · ongoing' : '')}
                </div>
                {cs.description && <p className="text-xs text-soft mt-0.5">{cs.description}</p>}
              </div>
              {editing && (
                <div className="flex gap-1 shrink-0">
                  <Button type="button" size="sm" variant="ghost" onClick={() => startEdit(i)}><Pencil size={12} /></Button>
                  <Button type="button" size="sm" variant="ghost" onClick={() => remove(i)}><Trash2 size={12} /></Button>
                </div>
              )}
            </div>
          )}
        </div>
      ))}
      {adding && draft && (
        <CommunityServiceForm draft={draft} setDraft={setDraft} onSave={save} onCancel={cancel} />
      )}
      {editing && !adding && (
        <Button type="button" size="sm" variant="secondary" onClick={startAdd}>
          <Plus size={12} className="mr-1" />Add community service entry
        </Button>
      )}
    </div>
  );
}

function CommunityServiceForm({ draft, setDraft, onSave, onCancel }: { draft: CommunityServiceEntry; setDraft: (d: CommunityServiceEntry) => void; onSave: () => void; onCancel: () => void }) {
  return (
    <Card className="p-3 bg-forest-50/40 border-forest-200 space-y-2">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
        <div>
          <Label>Organization</Label>
          <Input value={draft.organization} onChange={(e) => setDraft({ ...draft, organization: e.target.value })} />
        </div>
        <div>
          <Label>Role</Label>
          <Input value={draft.role} onChange={(e) => setDraft({ ...draft, role: e.target.value })} />
        </div>
        <div>
          <Label>Start date</Label>
          <Input type="date" value={draft.start_date} onChange={(e) => setDraft({ ...draft, start_date: e.target.value })} />
        </div>
        <div>
          <Label>End date (optional)</Label>
          <Input type="date" value={draft.end_date ?? ''} onChange={(e) => setDraft({ ...draft, end_date: e.target.value || null })} />
        </div>
        <div>
          <Label>Total hours</Label>
          <Input type="number" min="0" value={draft.hours ?? 0} onChange={(e) => setDraft({ ...draft, hours: Number(e.target.value) })} />
        </div>
      </div>
      <div>
        <Label>Description</Label>
        <Textarea rows={2} value={draft.description} onChange={(e) => setDraft({ ...draft, description: e.target.value })} />
      </div>
      <div className="flex justify-end gap-2">
        <Button type="button" size="sm" variant="secondary" onClick={onCancel}><X size={12} className="mr-1" />Cancel</Button>
        <Button type="button" size="sm" onClick={onSave}><Check size={12} className="mr-1" />Save</Button>
      </div>
    </Card>
  );
}
